import { action, computed, makeAutoObservable, observable, runInAction } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { ToasterState } from 'src/AppState/ToasterState';
import { ComplianceAutomationType } from 'src/api_openapi/v2/compliance/automationSchemas';
import { apiCreateComplianceAutomationRequest } from 'src/api_openapi/v2/compliance/apiCreateComplianceAutomation';
import { apiDeleteComplianceAutomationRequest } from 'src/api_openapi/v2/compliance/apiDeleteComplianceAutomation';
import { apiGetComplianceAutomationRequest } from 'src/api_openapi/v2/compliance/apiGetComplianceAutomation';
import { apiTriggerComplianceAutomationRequest } from 'src/api_openapi/v2/compliance/apiTriggerComplianceAutomation';
import { NetDepositLimitConfigurationState } from 'src/components/DepositLimitTab/NetDepositLimit/NetDepositLimitConfigurationState';
import {
    buildGamstopPpAutomationDto,
    GAMSTOP_PP_AUTOMATION_NAME,
    GAMSTOP_PP_AUTOMATION_SOURCE,
    hydrateGamstopPpStateFromAutomation,
} from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositiveAutomation.mapper';
import {
    GamstopPpAutomatedActionType,
    GamstopPpCommunicationOnlyModeType,
    isApplyCustomLimitAction,
    isComplianceRestrictionAction,
    isDepositRestrictionAction,
    isNoAction,
    isSendCommunicationOnlyAction,
} from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositive.types';
import {
    DEFAULT_GAMSTOP_PP_COMPLIANCE_RESTRICTION_PERIOD,
    DEFAULT_GAMSTOP_PP_DEPOSIT_RESTRICTION_PERIOD,
} from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositiveRestrictionOptions';
import { FilterValueType } from 'src/components/RealityCheckForm/CustomDropdown';
import { z } from 'zod';

type AutomationFetchStateType =
    | { kind: 'idle' }
    | { kind: 'found'; data: ComplianceAutomationType }
    | { kind: 'missing' }
    | { kind: 'error'; message: string };

export class GamstopPreviouslyPositiveState {
    @observable public selectedAction: GamstopPpAutomatedActionType | null = null;
    @observable public communicationOnlyMode: GamstopPpCommunicationOnlyModeType = 'send-email-only';
    @observable public sendEmailEnabled = false;
    @observable public sendPopupEnabled = false;
    @observable public isSaving = false;
    @observable public isLoading = true;
    @observable public isAgeRestrictedLimitsEnabled = false;
    @observable public complianceRestrictionPeriod: string = DEFAULT_GAMSTOP_PP_COMPLIANCE_RESTRICTION_PERIOD;
    @observable public depositRestrictionPeriod: number | null = DEFAULT_GAMSTOP_PP_DEPOSIT_RESTRICTION_PERIOD;
    @observable public testAccountId = '';
    @observable public isTriggering = false;
    @observable public triggerFeedbackMessage: string | null = null;
    @observable public triggerFeedbackVariant: 'default' | 'destructive' = 'default';
    @observable private actionExecutionOrders: Partial<Record<string, number>> = {};
    @observable private automationFetchState: AutomationFetchStateType = { kind: 'idle' };
    @observable private formErrorMessage: string | null = null;

    public readonly limitConfigurationState: NetDepositLimitConfigurationState;
    public readonly limitConfigurationStateUnder25: NetDepositLimitConfigurationState;

    public constructor(
        private readonly appState: AppState,
        configState: ConfigState
    ) {
        this.limitConfigurationState = new NetDepositLimitConfigurationState(configState);
        this.limitConfigurationStateUnder25 = new NetDepositLimitConfigurationState(configState);
        makeAutoObservable(this);

        void this.loadConfiguration();
    }

    /** `true` = GET returned data, `false` = missing (404), `null` = load error / not loaded yet */
    @computed public get automationExists(): boolean | null {
        switch (this.automationFetchState.kind) {
            case 'found':
                return true;
            case 'missing':
                return false;
            case 'error':
            case 'idle':
                return null;
        }
    }

    @computed public get errorMessage(): string | null {
        if (this.formErrorMessage !== null) {
            return this.formErrorMessage;
        }

        if (this.automationFetchState.kind === 'error') {
            return this.automationFetchState.message;
        }

        return null;
    }

    @computed public get hasSelectedAction(): boolean {
        return this.selectedAction !== null;
    }

    @computed public get isCustomLimitSelected(): boolean {
        return isApplyCustomLimitAction(this.selectedAction);
    }

    @computed public get isComplianceRestrictionSelected(): boolean {
        return isComplianceRestrictionAction(this.selectedAction);
    }

    @computed public get isDepositRestrictionSelected(): boolean {
        return isDepositRestrictionAction(this.selectedAction);
    }

    @computed public get isCommunicationOnlySelected(): boolean {
        return isSendCommunicationOnlyAction(this.selectedAction);
    }

    @computed public get isNoActionSelected(): boolean {
        return isNoAction(this.selectedAction);
    }

    @computed public get isTestTriggerAvailable(): boolean {
        return !this.appState.isProdMode;
    }

    @computed public get canTriggerAutomation(): boolean {
        return this.automationExists === true && this.parsedTestAccountId !== null && !this.isTriggering;
    }

    @computed private get parsedTestAccountId(): number | null {
        const parsed = parseInt(this.testAccountId.trim(), 10);

        if (isNaN(parsed) || parsed <= 0) {
            return null;
        }

        return parsed;
    }

    @action public setTestAccountId = (value: string): void => {
        this.testAccountId = value;
        this.triggerFeedbackMessage = null;
    };

    @action public onUpdateComplianceRestrictionPeriod = (changedValue: FilterValueType): void => {
        if (typeof changedValue === 'string') {
            this.complianceRestrictionPeriod = changedValue;
        }
    };

    @action public onUpdateDepositRestrictionPeriod = (changedValue: FilterValueType): void => {
        const parsedType = z.number().nullable().safeParse(changedValue);

        if (parsedType.success) {
            this.depositRestrictionPeriod = parsedType.data;
        }
    };

    @action public setSelectedAction = (action: GamstopPpAutomatedActionType | null): void => {
        this.selectedAction = action;
        this.formErrorMessage = null;

        if (action === null || isSendCommunicationOnlyAction(action) || isNoAction(action)) {
            this.sendEmailEnabled = false;
            this.sendPopupEnabled = false;
        }
    };

    @action public setCommunicationOnlyMode = (mode: GamstopPpCommunicationOnlyModeType): void => {
        this.communicationOnlyMode = mode;
        this.formErrorMessage = null;
    };

    @action public setSendEmailEnabled = (enabled: boolean): void => {
        this.sendEmailEnabled = enabled;
        this.formErrorMessage = null;
    };

    @action public setSendPopupEnabled = (enabled: boolean): void => {
        this.sendPopupEnabled = enabled;
        this.formErrorMessage = null;
    };

    @action public enableAgeRestrictedLimits = (): void => {
        this.isAgeRestrictedLimitsEnabled = true;
    };

    @action public disableAgeRestrictedLimits = (): void => {
        this.isAgeRestrictedLimitsEnabled = false;
    };

    public getActionExecutionOrder = (key: string): number | undefined => {
        return this.actionExecutionOrders[key];
    };

    @action public setActionExecutionOrders = (orders: Partial<Record<string, number>>): void => {
        this.actionExecutionOrders = orders;
    };

    @action public loadConfiguration = async (options?: { silent?: boolean }): Promise<void> => {
        const silent = options?.silent === true;

        if (!silent) {
            this.isLoading = true;
        }
        this.formErrorMessage = null;

        try {
            const response = await apiGetComplianceAutomationRequest(
                this.appState.common.appEnvParams.gatewayUrl,
                { name: GAMSTOP_PP_AUTOMATION_NAME },
                { authorization: `Bearer ${this.appState.common.session.currentJwt}` }
            );
            console.log('response', response);
            runInAction(() => {
                if (response.status === 200) {
                    this.automationFetchState = { kind: 'found', data: response.body };
                    hydrateGamstopPpStateFromAutomation(this, response.body);
                    return;
                }

                if (response.status === 404) {
                    this.automationFetchState = { kind: 'missing' };
                    return;
                }

                this.automationFetchState = {
                    kind: 'error',
                    message:
                        response.body.error.message === ''
                            ? 'Failed to load automation configuration. Saving is disabled until reload succeeds.'
                            : response.body.error.message,
                };
            });
        } catch {
            runInAction(() => {
                this.automationFetchState = {
                    kind: 'error',
                    message: 'Failed to load Gamstop Previously Positive automation configuration.',
                };
            });
        } finally {
            if (!silent) {
                runInAction(() => {
                    this.isLoading = false;
                });
            }
        }
    };

    @action public saveConfiguration = async (): Promise<void> => {
        if (this.automationExists === null) {
            await this.loadConfiguration({ silent: true });
        }

        if (this.automationExists === null) {
            this.formErrorMessage = 'Cannot save until automation configuration is loaded successfully.';
            return;
        }

        if (this.selectedAction === null) {
            this.formErrorMessage = 'Select an automated action before saving.';
            return;
        }

        const automationDto = buildGamstopPpAutomationDto(this);

        if (automationDto === null) {
            this.formErrorMessage = 'Complete the selected action configuration before saving.';
            return;
        }

        this.isSaving = true;
        this.formErrorMessage = null;

        try {
            const headers = { authorization: `Bearer ${this.appState.common.session.currentJwt}` };
            const gatewayUrl = this.appState.common.appEnvParams.gatewayUrl;

            let canCreateNew = this.automationExists === false;

            if (this.automationExists === true) {
                const deleteResponse = await apiDeleteComplianceAutomationRequest(
                    gatewayUrl,
                    { name: GAMSTOP_PP_AUTOMATION_NAME },
                    headers
                );

                if (deleteResponse.status === 500) {
                    this.formErrorMessage = 'Failed to replace Gamstop Previously Positive automation configuration.';
                    return;
                }

                canCreateNew = true;
            }

            if (!canCreateNew) {
                this.formErrorMessage = 'Cannot create automation in the current load state.';
                return;
            }

            const response = await apiCreateComplianceAutomationRequest(
                gatewayUrl,
                { requestBody: automationDto },
                headers
            );

            if (response.status === 200) {
                ToasterState.get(this.appState.common).setToast({
                    variant: 'success',
                    title: 'Success',
                    message: 'Gamstop Previously Positive automation configuration saved.',
                });
                await this.loadConfiguration({ silent: true });
                return;
            }

            this.formErrorMessage = 'Failed to save Gamstop Previously Positive automation configuration.';
        } catch {
            this.formErrorMessage = 'Failed to save Gamstop Previously Positive automation configuration.';
        } finally {
            this.isSaving = false;
        }
    };

    @action public triggerAutomation = async (): Promise<void> => {
        const accountId = this.parsedTestAccountId;

        if (this.automationExists !== true) {
            this.triggerFeedbackMessage = 'Save the automation configuration before triggering.';
            this.triggerFeedbackVariant = 'destructive';
            return;
        }

        if (accountId === null) {
            this.triggerFeedbackMessage = 'Enter a valid account ID.';
            this.triggerFeedbackVariant = 'destructive';
            return;
        }

        this.isTriggering = true;
        this.triggerFeedbackMessage = null;

        try {
            const response = await apiTriggerComplianceAutomationRequest(
                this.appState.common.appEnvParams.gatewayUrl,
                {
                    requestBody: {
                        automation_name: GAMSTOP_PP_AUTOMATION_NAME,
                        config: {
                            universe: this.appState.common.appEnvParams.universe_api,
                            accountId,
                            ...(this.isComplianceRestrictionSelected ? {} : { source: GAMSTOP_PP_AUTOMATION_SOURCE }),
                        },
                    },
                },
                { authorization: `Bearer ${this.appState.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                this.triggerFeedbackMessage = `Automation triggered for account ${accountId}.`;
                this.triggerFeedbackVariant = 'default';
                return;
            }

            this.triggerFeedbackMessage = 'Failed to trigger Gamstop Previously Positive automation.';
            this.triggerFeedbackVariant = 'destructive';
        } catch {
            this.triggerFeedbackMessage = 'Failed to trigger Gamstop Previously Positive automation.';
            this.triggerFeedbackVariant = 'destructive';
        } finally {
            this.isTriggering = false;
        }
    };
}
